import { HttpClient } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Entry } from 'contentful';
import { APIReferenceOverviewPage } from 'src/app/model/shared/api-reference-overview-page';
import { ContentfulService } from 'src/app/service/contentful.service';
import { PageVersionService } from 'src/app/service/page-version.service';
import { SharedDataService } from 'src/app/service/shared-data.service';
import { ModelValidator } from 'src/app/validator/model-validator';
import htmlTitle from 'src/assets/json/redirects/html-title.json';

@Component({
  selector: 'app-api-reference-overview-page',
  templateUrl: './api-reference-overview-page.component.html',
  styleUrls: ['./api-reference-overview-page.component.scss']
})
export class APIReferenceOverviewPageComponent implements OnInit, OnDestroy {
  @Input() pageName = '';

  pageVersions: string[] = [];
  pageVersionServiceSubscription: any;
  apiReferenceOverviewPage: APIReferenceOverviewPage = {} as APIReferenceOverviewPage;

  constructor(private contentfulService: ContentfulService, private sharedDataService: SharedDataService, private pageVersionService: PageVersionService, private httpClient: HttpClient) {}

  ngOnInitWithPageTitle(pageTitle: string): void {
    // console.log('>>>> >>>> APIReferenceOverviewPageComponent invoked ngOnInitWithPageTitle');
    this.pageName = pageTitle;
    this.ngOnInit();
  }

  ngOnInit(): void {
    htmlTitle.filter((title) => {
      if (title.page === 'api-reference-overview') {
        this.apiReferenceOverviewPage.htmlTitle = title.htmlTitle;
      }
    });
    const versionQuery = { 'fields.title': this.pageName, select: 'sys.id,fields.version' };

    this.contentfulService.getPageVersions(versionQuery).then((orderRetrieveItems) => {
      this.pageVersions = orderRetrieveItems
        .filter((item: { fields: undefined }) => item.fields !== undefined)
        .map((item: { fields: { version: string } }) => item.fields.version)
        .sort();

      let selectedPageVersion = '';
      if (this.pageVersions.length > 0) {
        selectedPageVersion = this.pageVersions[0];
        // console.log(this.pageName + ' >>>> default page version : ' + selectedPageVersion);
      }

      // console.log('default selectedPageVersion : ', selectedPageVersion);
      this.getPageContentFromContentful(selectedPageVersion);
    });

    this.pageVersionServiceSubscription = this.pageVersionService.pageVersion.subscribe((pageVersionData) => {
      if (pageVersionData.pageName === this.pageName && pageVersionData.selectedPageVersion !== undefined && pageVersionData.selectedPageVersion !== '') {
        // console.log('pageVersionData : ', pageVersionData);
        this.getPageContentFromContentful(pageVersionData.selectedPageVersion);
      }
    });
  }

  getPageContentFromContentful(selectedPageVersion: string) {
    // console.log(this.pageName + ' >>>> select page version : ' + selectedPageVersion);

    const query = selectedPageVersion === '' ? { 'fields.title': this.pageName } : { 'fields.title': this.pageName, 'fields.version': selectedPageVersion };
    this.contentfulService.getPageContentResponse(query).then((orderRetrievePageResponse) => {
      this.apiReferenceOverviewPage.introductionSections = [];
      this.apiReferenceOverviewPage.ribbonTileSections = [];
      this.apiReferenceOverviewPage.webserviceSections = [];
      this.apiReferenceOverviewPage.interfaceDesignSections = [];
      this.apiReferenceOverviewPage.noteSections = [];
      this.apiReferenceOverviewPage.endpointsTableSections = [];
      this.apiReferenceOverviewPage.wsdlSections = [];
      this.apiReferenceOverviewPage.tableSections = [];

      this.mapBanner(orderRetrievePageResponse.includes.Entry);
      this.apiReferenceOverviewPage.banner.pageName = this.pageName;
      this.apiReferenceOverviewPage.banner.pageVersions = this.pageVersions;
      this.apiReferenceOverviewPage.banner.defaultPageVersion = selectedPageVersion;
      this.sharedDataService.addSharedData(this.apiReferenceOverviewPage.banner);
      // console.log(this.pageName + ' >>>> >>>> banner.pageVersions : ' + this.apiReferenceOverviewPage.banner.pageVersions);
      this.mapNoteSection(orderRetrievePageResponse.includes.Entry);
      this.mapPageInfo(orderRetrievePageResponse.items);
      this.mapSection(orderRetrievePageResponse.includes.Entry);
      // console.log(this.apiReferenceOverviewPage);
      this.mapTableSection(orderRetrievePageResponse.includes.Entry);
    });
  }

  private mapBanner(orderRetrievePageBannerEntry: Entry<any>[]) {
    orderRetrievePageBannerEntry
      .filter((entry) => ModelValidator.isBanner(entry))
      .forEach((entry) => {
        this.mapEntryToBanner(entry);
      });
  }

  private mapEntryToBanner(entry: Entry<any>) {

    let imageDetails: any = entry.fields['image'];
    let videoDetails: any = entry.fields['video'];

    this.apiReferenceOverviewPage.banner = {
      title: entry.fields['title'] as string,
      subTitle: entry.fields['subTitle'] as string,
      image: imageDetails.fields.file.url,
      video: videoDetails?.fields?.file?.url
    };
  }

  private mapPageInfo(items: Entry<any>[]) {
    items.forEach((entry: any) => {
      let editorDetails = entry.fields?.editorDetails?.split('#');
      this.apiReferenceOverviewPage.banner.lastUpdated = entry.fields?.lastUpdated;
      this.apiReferenceOverviewPage.banner.url = entry.fields?.url;
      this.apiReferenceOverviewPage.banner.editorEmail = editorDetails?.pop();
      this.apiReferenceOverviewPage.banner.editorShortName = editorDetails?.pop();
      this.apiReferenceOverviewPage.banner.editorFullName = editorDetails?.pop();
      this.apiReferenceOverviewPage.htmlTitle = entry.fields.htmlTitle;
    });
  }

  private mapSection(orderRetrievePageSectionEntry: Entry<any>[]) {
    orderRetrievePageSectionEntry
      .filter((entry) => ModelValidator.isSection(entry))
      .forEach((entry) => {
        switch (entry.fields['group']) {
          case 'introduction':
            this.mapEntryToSection(entry);
            break;
          case 'ribbon-tile':
            this.mapEntryToRibbonTileSection(entry);
            break;
          case 'webservice':
            this.mapEntryToWebserviceSection(entry);
            break;
          case 'interface-design':
            this.mapEntryToInterfaceDesignSection(entry);
            break;
          case 'wsdl':
            this.mapWSDLSectionToEntries(entry, entry.fields['xml']);
            break;
          case 'below-wsdl':
            this.mapWSDLSectionToEntries(entry, entry.fields['xml']);
            break;
          default:
            console.error('New group Found - ' + entry.fields['title']);
            break;
        }
      });
  }

  private mapEntryToSection(entry: Entry<any>) {
    this.apiReferenceOverviewPage.introductionSections.push({
      title: '',
      body: entry.fields['body'] as string,
      group: entry.fields['group'] as string,
      order: entry.fields['order'] as number
    });
    this.apiReferenceOverviewPage.introductionSections.sort((a, b) => a.order - b.order);
  }

  private mapEntryToWebserviceSection(entry: Entry<any>) {
    this.apiReferenceOverviewPage.webserviceSections.push({
      title: '',
      body: entry.fields['body'] as string,
      group: entry.fields['group'] as string,
      order: entry.fields['order'] as number
    });
    this.apiReferenceOverviewPage.webserviceSections.sort((a, b) => a.order - b.order);
  }

  private mapWSDLSectionToEntries(entry: Entry<any>, xml: any) {
    let url = '#';
    try {
      url = xml?.content.filter((xmlContent: { nodeType: string }) => xmlContent.nodeType === 'embedded-asset-block')[0].data?.target?.fields?.file.url;
    } catch (err) {
      // console.log('No XML Data URL found');
      url = '#';
    }

    this.httpClient.get(url, { responseType: 'text' }).subscribe({
      next: (data) => this.mapWSDLSectionToEntriesWithXMLData(entry, data),
      error: () => this.mapWSDLSectionToEntriesWithXMLData(entry, '')
    });
  }

  private mapWSDLSectionToEntriesWithXMLData(entry: Entry<any>, xmlData: string) {

    let givenTitle:any=entry.fields['title'] as string;
    let pageTitle=givenTitle.split('#').pop();

    this.apiReferenceOverviewPage.wsdlSections.push({
      title:pageTitle,
      body: entry.fields['body'] as string,
      group: entry.fields['group'] as string,
      order: entry.fields['order'] as number,
      subBody: entry.fields['subBody'] as string,
      xmlData: xmlData
    });
    this.apiReferenceOverviewPage.wsdlSections.sort((a, b) => a.order - b.order);
  }

  private mapEntryToInterfaceDesignSection(entry: Entry<any>) {

    let givenTitle:any=entry.fields['title'] as string;
    let pageTitle=givenTitle.split('#').pop();

    this.apiReferenceOverviewPage.interfaceDesignSections.push({
      title:pageTitle,
      body: entry.fields['body'] as string,
      group: entry.fields['group'] as string,
      order: entry.fields['order'] as number,
      category: entry.fields['category'] as string,
      heading: entry.fields['heading'] as string,
      noteSections: this.apiReferenceOverviewPage.noteSections.filter((noteSection) => noteSection.category === entry.fields['title'])
    });
    // console.log('test', this.apiReferenceOverviewPage.interfaceDesignSections);
    this.apiReferenceOverviewPage.interfaceDesignSections.sort((a, b) => a.order - b.order);
  }

  private mapEntryToRibbonTileSection(entry: Entry<any>) {
    let tBody: string[] = [];
    let pageBody=entry.fields['body'] as string;
    pageBody.split('@').forEach((apiArrow: any) => {
      if (apiArrow !== '') {
        tBody.push(apiArrow);
      }
    });

    let givenTitle:any=entry.fields['title'] as string;
    let pageTitle=givenTitle.split('#').pop();

    this.apiReferenceOverviewPage.ribbonTileSections.push({
      title: pageTitle,
      body: '',
      bodyArrows: tBody,
      order: entry.fields['order'] as number,
      group: entry.fields['group'] as string,
      heading: entry.fields['heading'] as string,
      subBody: entry.fields['subBody'] as string,
      sectionClass: entry.fields['sectionClass'] as string,
      noteSections: this.apiReferenceOverviewPage.noteSections.filter((noteSection) => noteSection.category === entry.fields['category'])
    });
    this.apiReferenceOverviewPage.ribbonTileSections.sort((a, b) => a.order - b.order);
  }

  private mapNoteSection(entry: Entry<any>[]) {
    entry.filter((entry) => ModelValidator.isNote(entry));
    entry.forEach((entry) => {
      if (entry.fields['group'] === 'limitations') {
        this.mapEntryToNoteSection(entry);
      }
    });
  }

  private mapEntryToNoteSection(entry: Entry<any>) {

    let givenTitle:any=entry.fields['title'] as string;
    let pageTitle=givenTitle.split('#').pop();

    this.apiReferenceOverviewPage.noteSections.push({
      title: pageTitle,
      body: entry.fields['body'] as string,
      group: entry.fields['group'] as string,
      category: entry.fields['category'] as string,
      order: entry.fields['order'] as number,
      fullTitle: entry.fields['title'] as string,
      iconClass: entry.fields['iconClass'] as string,
      noteClass: entry.fields['noteClass'] as string
    });
    this.apiReferenceOverviewPage.noteSections.sort((a, b) => a.order - b.order);
  }

  private mapTableSection(aPIReferenceOverviewPageTableSectionEntries: Entry<any>[]) {
    aPIReferenceOverviewPageTableSectionEntries
      .filter((entry) => ModelValidator.isTableSection(entry))
      .forEach((entry) => {
        switch (entry.fields['group']) {
          case 'endpoints':
            this.mapEntryToEndpointsSection(entry);
            break;
          case 'interface-design':
            this.mapEntryToInterfaceDesignTableSection(entry);
            break;
        }
      });
  }

  private mapEntryToEndpointsSection(entry: Entry<any>) {

    let givenTitle:any=entry.fields['title'] as string;
    let pageTitle=givenTitle.split('#').pop();


    this.apiReferenceOverviewPage.endpointsTableSections.push({
      title: pageTitle,
      body: entry.fields['body'] as string,
      group: entry.fields['group'] as string,
      order: entry.fields['order'] as number,
      table: entry.fields['table'] as string
    });
    this.apiReferenceOverviewPage.endpointsTableSections.sort((a, b) => a.order - b.order);
  }

  private mapEntryToInterfaceDesignTableSection(entry: Entry<any>) {

    let givenTitle:any=entry.fields['title'] as string;
    let pageTitle=givenTitle.split('#').pop();


    this.apiReferenceOverviewPage.tableSections.push({
      title: pageTitle,
      body: entry.fields['body'] as string,
      group: entry.fields['group'] as string,
      order: entry.fields['order'] as number,
      table: entry.fields['table'] as string,
      category: entry.fields['category'] as string,
      heading: entry.fields['heading'] as string,
      noteSections: this.apiReferenceOverviewPage.noteSections.filter((noteSection) => noteSection.category === entry.fields['category'])
    });
    this.apiReferenceOverviewPage.tableSections.sort((a, b) => a.order - b.order);
  }

  ngOnDestroy() {
    this.pageVersionServiceSubscription.unsubscribe();
  }
}
